import { type RenewalApproach } from 'apps/employer_renewals/types'
import { convertGrapeMoneyToNumber } from 'utils'
import { type EmployersRenewalsRenewalQuoteResults, type PostQuotingProspectsProspectIdQuotesQuoteIdAllowancesAllowancesItem as UpdateAllowanceItem, type QuotingQuoteAllowance } from 'utils/web_api.schemas'

export const convertAllowancesToAllowanceParams = (allowances: QuotingQuoteAllowance[]): UpdateAllowanceItem[] => {
  const isUsingDynamicAllowances = isDynamicAllowances(allowances)

  return allowances.map(allowance => {
    const allowanceClass = allowance.location_id && allowance.location_type
      ? {
          location_id: allowance.location_id,
          location_type: allowance.location_type,
        }
      : undefined

    const flatConfig = isUsingDynamicAllowances
      ? undefined
      : {
          employee_amount: convertGrapeMoneyToNumber(allowance.employee_amount!),
          spouse_amount: convertGrapeMoneyToNumber(allowance.spouse_amount!),
          dependent_amount: convertGrapeMoneyToNumber(allowance.dependent_amount!),
          max_dependent_count: allowance.max_dependent_count!,
        }

    const dynamicConfig = isUsingDynamicAllowances
      ? {
          coverage_percentile: allowance.coverage_percentile!,
          benchmark: allowance.benchmark!,
          employee_contribution_percentage: allowance.employee_contribution_percentage!,
          spouse_contribution_percentage: allowance.spouse_contribution_percentage!,
          dependent_contribution_percentage: allowance.dependent_contribution_percentage!,
        }
      : undefined

    return {
      class: allowanceClass,
      flat_config: flatConfig,
      dynamic_config: dynamicConfig,
      qme_treatment: allowance.qme_treatment,
    }
  })
}

export const isDynamicAllowances = (allowances: QuotingQuoteAllowance[] | UpdateAllowanceItem[]): boolean => {
  if (isQuotingQuoteAllowanceArray(allowances)) {
    return allowances.some(allowance => allowance.coverage_percentile != null)
  }

  return allowances.some(allowance => allowance.dynamic_config?.coverage_percentile != null)
}

export const getDynamicAllowance = (allowances: QuotingQuoteAllowance[] | UpdateAllowanceItem[]): QuotingQuoteAllowance | undefined => {
  if (isQuotingQuoteAllowanceArray(allowances)) {
    return allowances.find(allowance => allowance.coverage_percentile != null)
  }
}

// Helper function to safely get contribution percentages from either a QuotingQuoteAllowance or an UpdateAllowanceItem
export const getContributionPercentagesForDynamicAllowance = (allowance: QuotingQuoteAllowance | UpdateAllowanceItem | undefined): { employee: number, spouse: number, dependent: number } => {
  // QuotingQuoteAllowance and UpdateAllowanceItem percentages are 1-100, but we need to convert them to 0-1
  if (!allowance) return { employee: 1, spouse: 0.5, dependent: 0.5 }

  // Check if it's a QuotingQuoteAllowance (has direct properties)
  if ('employee_contribution_percentage' in allowance) {
    return {
      employee: (allowance.employee_contribution_percentage ?? 100) / 100,
      spouse: (allowance.spouse_contribution_percentage ?? 50) / 100,
      dependent: (allowance.dependent_contribution_percentage ?? 50) / 100,
    }
  }

  // Otherwise it's an UpdateAllowanceItem (has nested dynamic_config)
  const updateItem = allowance as UpdateAllowanceItem
  return {
    employee: (updateItem.dynamic_config?.employee_contribution_percentage ?? 100) / 100,
    spouse: (updateItem.dynamic_config?.spouse_contribution_percentage ?? 50) / 100,
    dependent: (updateItem.dynamic_config?.dependent_contribution_percentage ?? 50) / 100,
  }
}

export function isQuotingQuoteAllowanceArray (
  allowances: QuotingQuoteAllowance[] | UpdateAllowanceItem[]
): allowances is QuotingQuoteAllowance[] {
  return (
    Array.isArray(allowances) &&
    allowances.length > 0 &&
    (
      'employee_amount' in allowances[0] ||
      'coverage_percentile' in allowances[0]
    )
  )
}

export const renewalApproachFromAllowance = (allowance?: QuotingQuoteAllowance, quoteResults?: EmployersRenewalsRenewalQuoteResults): RenewalApproach | undefined => {
  if (!allowance || !quoteResults) {
    return
  }

  const allowanceCoveragePercentile = allowance.coverage_percentile
  const allowanceBenchmark = allowance.benchmark?.replace('on_thatch_', '')

  const upgradeBenchmark = quoteResults.upgraded?.benchmark
  const reduceBenchmark = quoteResults.reduced?.benchmark
  const customBenchmark = quoteResults.custom?.benchmark

  if (!allowanceCoveragePercentile || !allowanceBenchmark) {
    return
  }

  if (upgradeBenchmark &&
    upgradeBenchmark.metal_tier === allowanceBenchmark &&
    upgradeBenchmark.coverage_percentile === allowanceCoveragePercentile.toString()) {
    return 'upgrade'
  } else if (reduceBenchmark &&
    reduceBenchmark.metal_tier === allowanceBenchmark &&
    reduceBenchmark.coverage_percentile === allowanceCoveragePercentile.toString()) {
    return 'reduce'
  } else if (customBenchmark &&
    customBenchmark.metal_tier === allowanceBenchmark &&
    customBenchmark.coverage_percentile === allowanceCoveragePercentile.toString()) {
    return 'custom'
  } else {
    return 'match'
  }
}
